import { Box, InputAdornment, Popover, Stack, TextField, Tooltip } from "@mui/material";
import { useCallback, useRef } from "react";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Avatar } from "@src/appV2/lib/Avatar";
import { Button } from "@src/appV2/redesign/components/Button";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { useModalState } from "@src/lib/ui-react";

import { Icon } from "@src/appV2/redesign/components/Icon";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { type AccountSwitcherUser } from "../../Shared/AccountSwitcher/types";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { AccountSwitcherDropdownAdminMobile } from "./AccountSwitcherDropdownAdmin";
import { WorkplaceSelectorSheet } from "./WorkplaceSelectorSheet";

export function AccountSwitcherMobile() {
  const isRebrand = useIsRebrand();
  const anchorRef = useRef<HTMLDivElement>(null);
  const {
    switcherTitle,
    switcherSubtitle,
    displayAdminSwitcher,
    dropdownVisible,
    setDropdownVisible,
    onAdminSearchBarEntered,
    onDropdownMouseLeave,
    workerDetails,
    workplaceDetails,
    onSearchChanged,
    onUserClicked,
    onAdminClicked,
    hasSearchTerm,
  } = useAccountSwitcher();

  const { profile } = useSession();
  const sheetModalState = useModalState();

  const handleWorkplaceSelected = useCallback(
    (user: AccountSwitcherUser) => {
      sheetModalState.closeModal();
      onUserClicked(user);
    },
    [onUserClicked, sheetModalState]
  );

  const handlePopoverClose = useCallback(() => {
    onDropdownMouseLeave();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [onDropdownMouseLeave]);

  return (
    <Box ref={anchorRef} sx={{ flex: 1, minWidth: 0 }}>
      {displayAdminSwitcher ? (
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search agents or workplaces..."
          autoComplete="off"
          sx={(theme) => ({
            "& .MuiOutlinedInput-root": {
              backgroundColor: isRebrand
                ? theme.palette.background?.base
                : theme.palette.background?.inverted?.muted,
              color: isRebrand
                ? theme.palette.base?.base
                : theme.palette.navbar?.itemContentSelected,
              paddingRight: 0,
              "& fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.normal?.borderColor
                  : theme.palette.button?.outline?.inverted?.normal?.borderColor,
              },
              "&:hover fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.hover?.borderColor
                  : theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
              "&.Mui-focused fieldset": {
                borderColor: isRebrand
                  ? theme.palette.button?.outline?.hover?.borderColor
                  : theme.palette.button?.outline?.inverted?.hover?.borderColor,
              },
            },
            "& .MuiOutlinedInput-input": {
              color: isRebrand
                ? theme.palette.base?.base
                : theme.palette.navbar?.itemContentSelected,
              "&::placeholder": {
                color: isRebrand ? theme.palette.base?.muted : theme.palette.navbar?.itemContent,
                opacity: 1,
              },
            },
          })}
          onFocus={onAdminSearchBarEntered}
          onChange={(event) => {
            setDropdownVisible(true);
            onSearchChanged(event.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: 0, paddingLeft: 0 }}>
                <Tooltip title="Switch To Admin">
                  <span>
                    <IconButton
                      variant={isRebrand ? "link" : "linkInverted"}
                      iconType="person-male-formal"
                      sx={(theme) => ({
                        borderRadius: 0,
                        borderLeft: isRebrand
                          ? `1px solid ${theme.palette.button?.outline?.normal?.borderColor}`
                          : `1px solid ${theme.palette.button?.outline?.inverted?.normal?.borderColor}`,
                        paddingX: 3,
                        height: "100%",
                        "&:hover": {
                          backgroundColor: isRebrand
                            ? theme.palette.background?.base
                            : theme.palette.background?.inverted?.muted,
                          ...(isRebrand && {
                            borderLeft: `1px solid ${theme.palette.button?.outline?.normal?.borderColor}`,
                          }),
                        },
                      })}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAdminClicked();
                      }}
                    />
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      ) : (
        <Button
          fullWidth
          variant={isRebrand ? "outlined" : "outlineInverted"}
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: "none",
          }}
          onClick={sheetModalState.openModal}
        >
          <Avatar
            name={switcherTitle}
            size="small"
            sx={(theme) => ({
              bgcolor: theme.palette.navbar?.avatarBackground,
              color: theme.palette.navbar?.avatarText,
              flexShrink: 0,
            })}
          />
          <Stack direction="column" flex={1} minWidth={0}>
            <Text
              variant="body1"
              textAlign="left"
              color={(theme) =>
                isRebrand ? theme.palette.base?.base : theme.palette.navbar?.userName
              }
              noWrap
            >
              {switcherTitle}
            </Text>
            {switcherSubtitle && (
              <Text variant="caption" textAlign="left" noWrap>
                {switcherSubtitle}
              </Text>
            )}
          </Stack>
          <Icon
            type="caret-bottom"
            sx={(theme) => ({
              color: isRebrand
                ? theme.palette.button?.outline.hover.color
                : theme.palette.button?.outline.inverted.hover.color,
              flexShrink: 0,
            })}
          />
        </Button>
      )}

      {displayAdminSwitcher ? (
        <Popover
          open={dropdownVisible}
          anchorEl={anchorRef.current}
          onClose={handlePopoverClose}
          disableAutoFocus
          disableEnforceFocus
          marginThreshold={0}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          slotProps={{
            paper: {
              sx: (theme) => ({
                width: anchorRef.current?.offsetWidth,
                maxHeight: "60vh",
                overflowY: "auto",
                background: theme.palette.navbar?.background,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.4)",
                paddingX: 2,
                paddingY: 2,
              }),
            },
          }}
        >
          <AccountSwitcherDropdownAdminMobile
            workerDetails={workerDetails}
            workplaceDetails={workplaceDetails}
            onUserClicked={onUserClicked}
            hasSearchTerm={hasSearchTerm}
          />
        </Popover>
      ) : (
        <WorkplaceSelectorSheet
          modalState={sheetModalState}
          workplaceDetails={workplaceDetails}
          selectedWorkplaceId={profile?.userId}
          onWorkplaceSelected={handleWorkplaceSelected}
        />
      )}
    </Box>
  );
}
